import { HttpEvent, HttpHandler, HttpInterceptor, HttpParams, HttpRequest } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import { Observable, of, ReplaySubject } from 'rxjs';
import { map, switchMap, take } from 'rxjs/operators';

import { Router } from '@angular/router';
import { getRouterShareId, ShareApiInterceptor } from '@mediahaven/shared';
import { TenantInfoDataService } from '@stylehaven/components';

@Injectable()
export class PortalApiInterceptor extends ShareApiInterceptor implements HttpInterceptor {
    private authKey$: ReplaySubject<string> = new ReplaySubject<string>(1);

    private readonly tenantInfoService: TenantInfoDataService = inject(TenantInfoDataService);

    private readonly router: Router = inject(Router);

    constructor() {
        super();
        this.init();
    }

    private init(): void {
        const authKey$: Observable<string | undefined> = this.tenantInfoService.getPortalApiKey();

        authKey$.pipe(take(1)).subscribe(authKey => {
            if (!authKey) {
                throw new Error('API Authentication Key not provided in library config');
            }

            this.authKey$.next(authKey);
        });
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.requestHandlerSwitch(request).pipe(switchMap(tokenRequest => next.handle(tokenRequest)));
    }

    private requestHandlerSwitch(request: HttpRequest<any>): Observable<HttpRequest<any>> {
        const isMediaHavenApi: boolean = request.url.includes(`mediahaven-rest-api`);
        const isCurrentUserRequest: boolean = request.url.includes('users/current');

        const isSharingContext: boolean = !!getRouterShareId(this.router);

        if (isMediaHavenApi && (!isSharingContext || isCurrentUserRequest)) {
            return this.handleAuthKey(request);
        }

        return of(this.handleShareToken(request));
    }

    private handleAuthKey(request: HttpRequest<any>): Observable<HttpRequest<any>> {
        const url: string = request.url.replace('mediahaven-rest-api', 'portal-api');
        const params: HttpParams = request.params || new HttpParams();

        return this.authKey$.pipe(
            map(authKey => params.set('key', authKey)),
            map(params => request.clone({ url, params }))
        );
    }
}
